import {
    APP_ID,
    APP_INITIALIZER,
    CUSTOM_ELEMENTS_SCHEMA,
    ErrorHandler,
    Injector,
    NgModule,
    TransferState,
    inject,
    provideAppInitializer,
} from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { RouterModule } from '@angular/router';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CustomErrorHandlerService } from '@app/exceptions/custom-error-handler.service';
import { ErrorPipelineFactory } from '@app/exceptions/pipeline/error-pipeline.factory';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { BrowserStateInterceptor } from './core/services/http-interceptors/browserstate.interceptor';
import { UserInterceptor } from '@app/transactions/pages/user-info/user.interceptor';
import { TransactionInterceptor } from '@app/transactions/pages/transaction-info/transaction.interceptor';
import { TokenStorage } from './get-token';
import { JwtModule } from '@auth0/angular-jwt';
import { PlatformBrowserService } from './core/services/platform-browser.service';
import { GlobalStateService } from './core/services/global-state.service';
import { TranslateModule, TranslateLoader, TranslateCompiler } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { AuthFactorErrorInterceptor } from './my/auth-factor-error.interceptor';
import { OAuthModule } from 'angular-oauth2-oidc';
import { ServiceLoader } from './core/services/service-loader';
import { pathsSaldo } from 'environments/paths-saldo';
import { DialogErrorRedirectModule } from './core/components/dialog-error-redirect/dialog-error-redirect.module';
import { DashboardLazyLoadService } from './dashboard/dashboard-lazy-load.service';
import { translateBrowserLoaderFactory } from './translate/translate-browser.loader';
import { CoreModule } from './core/core.module';
import {
    provideNgxJsonapiStandalone,
    StoreService,
    JSONAPI_STORE_SERVICE,
    JsonRipper,
    JSONAPI_RIPPER_SERVICE,
    JsonapiCore,
} from 'ngx-jsonapi';
import { JamRefreshService, provideNgxJsonapiMaterial } from 'ngx-jsonapi-material';
import { provideCacheableAnimationLoader, provideLottieOptions } from 'ngx-lottie';
import { ApiErrorHandlingService } from './exceptions/services/api-error-handling.service';
import { MatDialogModule } from '@angular/material/dialog';

import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { SwiperDirective } from './core/directives/swiper.directive';
import { OAuthInterceptorModule } from './my/oauth-interceptor.module';
import { MarkdownModule } from 'ngx-markdown';
import { ERROR_REPORTING_PROVIDER } from './exceptions/reporting/error-reporting.provider';
import { BREADCRUMB_TRACKER_PROVIDER } from './exceptions/reporting/breadcrumbs/breadcrumb-tracker.provider';
import { BypassRecaptchaInterceptor } from './guest/bypass-recaptcha.interceptor';
import { StoragePort } from '@app/common/storage/application/ports/storage.port';
import { LocalePersistencePort } from '@app/common/locale/application/ports/locale-persistence.port';
import {
    DASHBOARD_USER_FACADE_PORT,
    DASHBOARD_USERS_REPOSITORY_PORT,
} from '@app/common/locale/application/ports/dashboard-locale.dependencies';
import { LocalStorageService as LocaleLocalStorageService } from '@ui/features/storage/infrastructure/storage/local-storage.service';
import { BackendLocalePersistence } from '@ui/features/locale/infrastructure/persistence/backend-locale.persistence';
import { LocaleInterceptor } from '@ui/shared/interceptors/locale.interceptor';
import { StandardHeadersInterceptor } from '@ui/shared/interceptors/standard-headers.interceptor';
import { UserFacade } from '../common/application/user/user.facade';
import { UsersService } from './core/services/users.service';
import { SeoMetaPort } from '@ui/features/route-config/infrastructure/seo-meta.port';
import { AngularSeoMetaAdapter } from '@ui/features/route-config/infrastructure/angular-seo-meta.adapter';
import { RoutesRepository } from '@ui/features/route-config/application/repositories/routes.repository';
import { ApplyRouteSeoUseCase } from '@ui/features/route-config/application/use-cases/apply-route-seo.use-case';
import { ApplyRouteLocaleUrlUseCase } from '@ui/features/route-config/application/use-cases/apply-route-locale-url.use-case';
import { LocaleUiSyncService } from '@ui/features/locale/application/services/locale-ui-sync.service';
import { RouteBootstrapService } from '@ui/shared/services/route-bootstrap/route-bootstrap.service';
import { I18nBootstrapService } from '@ui/shared/services/i18n-bootstrap/i18n-bootstrap.service';
import { SYSTEMS_PROVIDERS } from '@ui/features/systems/infrastructure/providers/systems.provider';

export function playerFactory(): Promise<any> {
    return import('lottie-web');
}

@NgModule({
    declarations: [AppComponent, SwiperDirective],
    imports: [
        RecaptchaV3Module,
        OAuthInterceptorModule.forRoot(),
        BrowserAnimationsModule,
        RouterModule,

        MatDialogModule,
        MarkdownModule.forRoot(),
        OAuthModule.forRoot(),
        DialogErrorRedirectModule,
        HttpClientModule,
        CoreModule,
        JwtModule.forRoot({
            config: {
                tokenGetter: TokenStorage.getToken,
                allowedDomains: environment.WHITE_LISTED_DOMAINS,
            },
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: translateBrowserLoaderFactory,
                deps: [TransferState],
            },
            compiler: {
                provide: TranslateCompiler,
                useClass: TranslateMessageFormatCompiler,
            },
        }),
        AppRoutingModule,
    ],
    providers: [
        provideLottieOptions({ player: playerFactory }),
        provideCacheableAnimationLoader(),
        provideNgxJsonapiStandalone({
            url: environment.API_URL + pathsSaldo.API_PATH,
        }),
        { provide: JSONAPI_STORE_SERVICE, useClass: StoreService },
        { provide: JSONAPI_RIPPER_SERVICE, useClass: JsonRipper },
        { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.TOKEN_RECAPTCHA_SITE_KEY_V3 },
        JamRefreshService,
        ...provideNgxJsonapiMaterial(),
        {
            provide: APP_INITIALIZER,
            useFactory: (i18nBootstrap: I18nBootstrapService) => (): Promise<void> =>
                i18nBootstrap.initialize(),
            deps: [I18nBootstrapService],
            multi: true,
        },
        { provide: APP_ID, useValue: 'serverApp' },
        GlobalStateService,
        DashboardLazyLoadService,
        ErrorPipelineFactory,
        { provide: ErrorHandler, useClass: CustomErrorHandlerService },
        ERROR_REPORTING_PROVIDER,
        BREADCRUMB_TRACKER_PROVIDER,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LocaleInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BrowserStateInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: StandardHeadersInterceptor,
            multi: true,
        },
        { provide: HTTP_INTERCEPTORS, useClass: TransactionInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ApiErrorHandlingService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: UserInterceptor, multi: true },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthFactorErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BypassRecaptchaInterceptor,
            multi: true,
        },
        {
            provide: StoragePort,
            useExisting: LocaleLocalStorageService,
        },
        {
            provide: LocalePersistencePort,
            useExisting: BackendLocalePersistence,
        },
        {
            provide: DASHBOARD_USER_FACADE_PORT,
            useExisting: UserFacade,
        },
        {
            provide: DASHBOARD_USERS_REPOSITORY_PORT,
            useExisting: UsersService,
        },
        { provide: SeoMetaPort, useClass: AngularSeoMetaAdapter },
        RoutesRepository,
        ApplyRouteSeoUseCase,
        ApplyRouteLocaleUrlUseCase,
        ...SYSTEMS_PROVIDERS,
        {
            provide: APP_INITIALIZER,
            useFactory: (routeBootstrapService: RouteBootstrapService) => (): void =>
                routeBootstrapService.initialize(),
            deps: [RouteBootstrapService],
            multi: true,
        },
        // Instancia el sync estado de locale -> idioma de UI (browser y SSR) antes del primer render.
        provideAppInitializer((): void => {
            inject(LocaleUiSyncService);
        }),
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
})
export class AppModule {
    public constructor(
        public jsonapiCore: JsonapiCore,
        private browserService: PlatformBrowserService,
        private injector: Injector,
    ) {
        ServiceLoader.injector = this.injector;
        // Se necesita instanciar la clase para poder verificar si estamos en el browser
        /* eslint-disable @typescript-eslint/no-unused-expressions */
        new TokenStorage(this.browserService);
        /* eslint-enable @typescript-eslint/no-unused-expressions */
    }
}
